import {RouteEntity, sitesSelector, useGetAllSitesByRouteQuery} from "@ots-frontend/ots-data";
import {ActionIcon, Card, Container, Divider, Grid, Group, Modal, Text, Title} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {DefaultText} from "../../text/DefaultText/DefaultText";
import {useMemo} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {useApp} from "../../../providers/RegionProvider/RegionProvider";
import {SiteDataTable} from "../../data-table/SiteDataTable/SiteDataTable";
import {HasAccess, RestrictionDataTableCard} from "@ots-frontend/ots-ui";
import {useDisclosure} from "@mantine/hooks";
import {IconHierarchy} from "@tabler/icons-react";
import {EntityGraphView} from "../../graph/EntityGraphView/EntityGraphView";

export type RouteDetailsProps = {
  route: RouteEntity
}

export const RouteDetails = (props: RouteDetailsProps) => {
  const {route} = props;

  const {regionId} = useApp()
  const {t} = useTranslation(["translation", "common"]);

  const [opened, { close, open }] = useDisclosure(false);

  const selectSites = useMemo(() => {
    return sitesSelector();
  }, []);

  const {sites, isSitesLoading} = useGetAllSitesByRouteQuery(regionId ? {
    regionId: regionId,
    routeId: route.id,
  } : skipToken, {
    selectFromResult: (result) => ({
      sites: selectSites(result, {}),
      isSitesLoading: result.isLoading
    })
  });

  return (
    <Grid>
      <Grid.Col span={6}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>

          <Group position="apart" pb="sm">
            <Title order={4}>{t("profile.route.info.section.details.label", "Details")}</Title>
            <ActionIcon onClick={() => open()}>
              <IconHierarchy size="1.125rem"/>
            </ActionIcon>
          </Group>

          <Divider/>

          <Grid>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.route.info.field.name.label", "Name")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {route.name}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.route.info.field.company.label", "Company")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {route.companyId ? <DefaultText value={route.companyName}/> : undefined}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.route.info.field.group.label", "Route Group")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {route.routeGroupId ? <DefaultText value={route.routeGroupName}/> : undefined}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.route.info.field.usable.label", "Usable")}
              </Text>
              <Text ta="left" c={route.doNotUse ? "warning" : "dimmed"} fz="xs">
                {route.doNotUse ? t("common.shared.no", "No") : t("common.shared.yes", "Yes")}
              </Text>
            </Grid.Col>
          </Grid>
        </Card>
      </Grid.Col>

      <HasAccess permissions={["restriction_view"]}>
        <Grid.Col span={12}>
          <RestrictionDataTableCard entity={route}/>
        </Grid.Col>
      </HasAccess>

      <Grid.Col span={12}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group position="apart" pb="sm">
            <Title order={4}>
              {t("common.entity.site.plural", "Sites")}
            </Title>
          </Group>
          <Divider pb="sm"/>
          <SiteDataTable
            withBorder
            records={sites}
            fetching={isSitesLoading}
          />
        </Card>
      </Grid.Col>

      {opened &&
        <Modal opened={opened} onClose={close} size="auto" title="Entity Graph">
          <Container h={700} w={800}>
            <EntityGraphView entity={route}/>
          </Container>
        </Modal>
      }
    </Grid>
  );
};

